import { Tooltip } from "@mui/material";
import { COLORS } from "../assets/theme";
import { IProfile } from "../services/ProfileService";
import { Option } from "../types/table/interfaces.type";
import { FieldType } from "./common/CustomDialog";
import AutoComplete from "./common/AutoComplete";

export const getStepsData = (
  dialogData: Partial<IProfile>,
  errors: Record<string, boolean>,
  setDialogData: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  userOptions: Option[],
  countriesOptions: { label: string; value: string | number }[],
  currencyOptions: { id: string | number; name: string }[],
  paymentTypesOptions: { label: string; value: string | number }[],
  bilingTermOptions: { label: string; value: string | number }[],
  invoiceTemplateOptions: { label: string; value: string | number }[],
  handleFileChange: (file: File | null) => void
): FieldType[][] => [
  [
    {
      type: "text",
      label: "Profile Name",
      placeholder: "Name",
      value: dialogData.name ?? "",
      error: errors.name || false,
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      helperText: errors.name ? "This field is required." : "",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, name: stringValue }));
        setErrors((prev) => ({ ...prev, name: !stringValue }));
      },
    },
    {
      type: "custom",
      component: (
        <AutoComplete
          label="Employee"
          value={dialogData.userId ?? ""}
          options={userOptions}
          onChange={(value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              setDialogData((prev) => ({
                ...prev,
                userId: value,
              }));
              setErrors((prev) => ({ ...prev, userId: !value }));
            }
          }}
          clearable={true}
          error={errors.userId || false}
          placeholder="Select Employee"
          highlightLabelStar={true}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
      ),
    },
    {
      type: "text",
      label: "Address",
      placeholder: "Address",
      value: dialogData.address ?? "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      error: errors.address || false,
      helperText: errors.address ? "This field is required." : "",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, address: stringValue }));
        setErrors((prev) => ({ ...prev, address: !stringValue }));
      },
    },

    {
      type: "text",
      label: "Account Manager Email",
      placeholder: "Account Manager Email",
      value: dialogData.accountManagerEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      typeinput: "email",
      error: errors.accountManagerEmail || false,
      helperText: errors.accountManagerEmail ? "Invalid email format." : "",
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          accountManagerEmail: isError,
        }));
      },
      validateOnChange: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({
          ...prev,
          accountManagerEmail: stringValue,
        }));
      },
    },
    {
      type: "text" as const,
      label: "Phone Number",
      placeholder: "Phone Number",
      error: errors.phoneNumber || false,
      helperText: errors.phoneNumber ? "This field is required." : "",
      value: dialogData.phoneNumber ?? "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, phoneNumber: stringValue }));
        setErrors((prev) => ({ ...prev }));
      },
    },

    {
      type: "text" as const,
      label: "Rating Email",
      placeholder: "Rating Email",
      typeinput: "email",
      value: dialogData.ratingEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          ratingEmail: isError,
        }));
      },
      validateOnChange: true,
      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, ratingEmail: stringValue }));
      },
      error: errors.ratingEmail || false,
      helperText: errors.ratingEmail ? "Invalid email format." : "",
    },
    {
      type: "custom",
      component: (
        <AutoComplete
          label="Country"
          value={dialogData.countryId ?? ""}
          options={countriesOptions}
          onChange={(value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              const stringValue = value.toString();
              setDialogData((prev) => ({
                ...prev,
                countryId: stringValue,
              }));
              setErrors((prev) => ({ ...prev, country: !stringValue }));
            }
          }}
          clearable={true}
          error={errors.country || false}
          placeholder="Select Country"
          highlightLabelStar={true}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
      ),
    },
    {
      type: "text" as const,
      label: "Billing Email",
      placeholder: "Billing Email",
      typeinput: "email",
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          billingEmail: isError,
        }));
      },
      validateOnChange: true,
      value: dialogData.billingEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, billingEmail: stringValue }));
      },
      error: errors.billingEmail || false,
      helperText: errors.billingEmail ? "Invalid email format." : "",
    },
    {
      type: "custom",
      component: (
        <Tooltip
          title={
            dialogData.id
              ? "Currency cannot be changed after creation"
              : ""
          }
          placement="top"
          arrow
          sx={{ width: "100%" }}
        >
          <span style={{ width: "100%", display: "block" }}>
            <AutoComplete
              label="Currency"
              value={dialogData.currencyId ?? ""}
              options={currencyOptions.map((currency) => ({
                label: currency.name,
                value: currency.id,
              }))}
              onChange={(value: string | number | (string | number)[]) => {
                if (typeof value === "string" || typeof value === "number") {
                  const stringValue = value.toString();
                  setDialogData((prev) => ({
                    ...prev,
                    currencyId: stringValue,
                  }));
                  setErrors((prev) => ({ ...prev, currencyIdOptions: !stringValue }));
                }
              }}
              clearable={true}
              error={errors.currency || false}
              placeholder="Select Currency"
              highlightLabelStar={true}
              disabled={!!dialogData.id}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.WhiteBackground,
                },
              }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      type: "text" as const,
      label: "Daily Report Email",
      placeholder: "Daily Report Email",
      typeinput: "email",
      bgColor: COLORS.WhiteBackground,
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          dailyReportEmail: isError,
        }));
      },
      validateOnChange: true,
      value: dialogData.dailyReportEmail ?? "",

      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, dailyReportEmail: stringValue }));
      },
      error: errors.dailyReportEmail || false,
      helperText: errors.dailyReportEmail ? "Invalid email format." : "",
    },

    {
      type: "text" as const,
      label: "Website",
      placeholder: "Website",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.website ?? "",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, website: stringValue }));
        setErrors((prev) => ({ ...prev, website: !stringValue }));
      },
    },
    {
      type: "text" as const,
      label: "Support Email",
      placeholder: "Support Email",
      typeinput: "email",
      value: dialogData.supportEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          supportEmail: isError,
        }));
      },
      validateOnChange: true,
      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, supportEmail: stringValue }));
      },
      error: errors.supportEmail || false,
      helperText: errors.supportEmail ? "Invalid email format." : "",
    },
    {
      type: "text" as const,
      label: "SID Registration  Email",
      placeholder: "SID Registration  Email",
      typeinput: "email",
      value: dialogData.sidRegistrationEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          sidRegistrationEmail: isError,
        }));
      },
      validateOnChange: true,
      highlightLabelStar: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({
          ...prev,
          sidRegistrationEmail: stringValue,
        }));
      },
      error: errors.sidRegistrationEmail || false,
      helperText: errors.sidRegistrationEmail ? "Invalid email format." : "",
    },
    {
      type: "text" as const,
      label: "Rate Updates Email",
      placeholder: "Rate Updates Email",
      typeinput: "email",
      value: dialogData.rateUpdatesEmail ?? "",
      bgColor: COLORS.WhiteBackground,
      onValidation: (isError) => {
        setErrors((prevErrors) => ({
          ...prevErrors,
          rateUpdatesEmail: isError,
        }));
      },
      validateOnChange: true,
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({
          ...prev,
          rateUpdatesEmail: stringValue,
        }));
      },
      error: errors.rateUpdatesEmail || false,
      helperText: errors.rateUpdatesEmail ? "Invalid email format." : "",
    },
    {
      type: "switch" as const,
      label: "Uses VPN",
      value: dialogData.usesVpn ?? false,
      onChange: (value: boolean) => {
        setDialogData((prev) => ({ ...prev, usesVpn: value }));
      },
    },
    {
      type: "text" as const,
      hidden: true,
      value: dialogData.website ?? "",
      onChange: (value: any) => {},
    },
  ],
  [
    {
      type: "text",
      typeinput: "number",
      label: "Client Initial Balance",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.clientInitialBalance ?? "",
      onChange: (value) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          clientInitialBalance: numericValue,
        }));
      },
      placeholder: "Client Initial Balance",
      error: !!errors.clientInitialBalance,
      helperText: errors.clientInitialBalance
        ? "Client Initial Balance is required"
        : "",
    },

    {
      type: "text",
      typeinput: "number",
      tooltip: "Consumption percentage",
      label: "Alert 1",
      placeholder: "Alert 1",
      endAdornment: "%",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.clientAlert1 ?? "",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          clientAlert1:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
      helperText: errors.clientAlert1 ? "Alert 1 is required" : "",
    },
    {
      type: "text",
      label: "Client Balance Limit",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.clientCreditLimit ?? "",
      placeholder: "Client Balance Limit",
      error: errors.clientCreditLimit || false,
      helperText: errors.clientCreditLimit ? "This field is required." : "",
      typeinput: "number",
      onChange: (value) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          clientCreditLimit: numericValue,
        }));
      },
    },
    {
      type: "text",
      label: "Alert 2",
      bgColor: COLORS.WhiteBackground,
      tooltip: "Consumption percentage",
      endAdornment: "%",
      value: dialogData.clientAlert2 ?? "",
      placeholder: "Alert 2",
      error: errors.clientAlert2 || false,
      helperText: errors.clientAlert2 ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          clientAlert2:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
    },
    {
      type: "text",
      label: "Flex Amount",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.flexAmount ?? "",
      placeholder: "Flex Amount",
      highlightLabelStar: true,
      error: errors.flexAmount || false,
      helperText: errors.flexAmount ? "This field is required." : "",
      typeinput: "number",
      onChange: (value) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          flexAmount: numericValue,
        }));
      },
    },
    {
      type: "text",
      label: "Alert 3",
      bgColor: COLORS.WhiteBackground,
      tooltip: "Consumption percentage",
      endAdornment: "%",
      value: dialogData.clientAlert3 ?? "",
      placeholder: "Alert 3",
      error: errors.clientAlert3 || false,
      helperText: errors.clientAlert3 ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          clientAlert3:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
    },
  ],
  [
    {
      type: "text" as const,
      label: "Provider Initial Balance",
      placeholder: "Provider Initial Balance",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.providerInitialBalance ?? "",
      error: errors.providerInitialBalance || false,
      helperText: errors.providerInitialBalance
        ? "Provider Initial Balance is required"
        : "",
      typeinput: "number",
      onChange: (value: string | number) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          providerInitialBalance: numericValue,
        }));
      },
    },
    {
      type: "text",
      label: "Alert 1",
      value: dialogData.providerAlert1 ?? "",
      placeholder: "Alert 1",
      tooltip: "Consumption percentage",
      bgColor: COLORS.WhiteBackground,
      endAdornment: "%",
      error: errors.providerAlert1 || false,
      helperText: errors.providerAlert1 ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          providerAlert1:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
    },
    {
      type: "text" as const,
      label: "Provider Balance Limit",
      placeholder: "Provider Balance Limit",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.providerCreditLimit ?? "",
      error: errors.providerCreditLimit || false,
      helperText: errors.providerCreditLimit ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          providerCreditLimit: numericValue,
        }));
      },
    },

    {
      type: "text" as const,
      label: "Alert 2",
      placeholder: "Alert 2",
      endAdornment: "%",
      tooltip: "Consumption percentage",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.providerAlert2 ?? "",
      error: errors.providerAlert2 || false,
      helperText: errors.providerAlert2 ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          providerAlert2:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
    },
    {
      type: "text" as const,
      hidden: true,
      value: dialogData.website ?? "",

      onChange: (value: any) => {},
    },
    {
      type: "text" as const,
      label: "Alert 3",
      placeholder: "Alert 3",
      tooltip: "Consumption percentage",
      value: dialogData.providerAlert3 ?? "",
      error: errors.providerAlert3 || false,
      bgColor: COLORS.WhiteBackground,
      endAdornment: "%",
      helperText: errors.providerAlert3 ? "This field is required." : "",
      typeinput: "number",
      onChange: (value: string | number) =>
        setDialogData((prev) => ({
          ...prev,
          providerAlert3:
            typeof value === "string" && value.trim() === ""
              ? ""
              : Math.min(Number(value), 99),
        })),
    },
  ],

  [
    {
      type: "text",
      label: "Payment Term",
      placeholder: "Payment Term",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      error: errors.paymentTerm || false,
      helperText: errors.paymentTerm
        ? "This field is required and must be a number."
        : "",
      value: dialogData.paymentTerm ?? "",
      typeinput: "number",
      onChange: (value: string | number) => {
        const numericValue = value === "" ? undefined : Number(value);
        setDialogData((prev) => ({
          ...prev,
          paymentTerm: numericValue,
        }));
      },
    },
    {
      type: "select" as const,
      label: "Payment Type",
      placeholder: "Select Payment Type",
      options: paymentTypesOptions,
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.paymentType ?? "",
      onChange: (value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              const stringValue = value.toString();
              setDialogData((prev) => ({
                ...prev,
                paymentType: stringValue,
              }));
              setErrors((prev) => ({
                ...prev,
                paymentTypesOptions: !stringValue,
              }));
            }
      },

      error: errors.paymentType || false,
      helperText: errors.paymentType ? "This field is required." : "",
    },
    {
      type: "select" as const,
      label: "Billing Term",
      placeholder: "Select Belling Term",
      options: bilingTermOptions,
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      value: dialogData.billingTerm ?? "",
      onChange: (value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              const stringValue = value.toString();
              setDialogData((prev) => ({
                ...prev,
                billingTerm: stringValue,
              }));
              setErrors((prev) => ({ ...prev, country: !stringValue }));
            }
      },

      error: errors.billingTerm || false,
      helperText: errors.billingTerm ? "This field is required." : "",
    },
    {
      type: "custom",
      component: (
        <AutoComplete
          label="Invoice template Name"
          value={dialogData.invoiceTemplateId ?? ""}
          options={invoiceTemplateOptions}
          onChange={(value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              const stringValue = value.toString();
              setDialogData((prev) => ({
                ...prev,
                invoiceTemplateId: stringValue,
              }));
              setErrors((prev) => ({ ...prev, country: !stringValue }));
            }
          }}
          clearable={true}
          error={errors.invoiceTemplateId || false}
          placeholder="Select Invoice template Name"
          highlightLabelStar={true}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
      ),
    },
  ],
  [
    {
      type: "text",
      label: "Beneficiary Name",
      value: dialogData.beneficiaryName ?? "",
      placeholder: "Beneficiary Name",
      bgColor: COLORS.WhiteBackground,

      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, beneficiaryName: stringValue }));
      },
    },
    {
      type: "text",
      label: "IBAN",
      value: dialogData.iban ?? "",
      placeholder: "IBAN",
      bgColor: COLORS.WhiteBackground,

      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, iban: stringValue }));
      },
    },
    {
      type: "text",
      label: "Bank Name",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.bankName ?? "",
      placeholder: "Bank Name",

      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, bankName: stringValue }));
      },
    },
    {
      type: "text",
      label: "Swift Code",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.swiftCode ?? "",
      placeholder: "Swift Code",
      onChange: (value: string | number) => {
        setDialogData((prev) => ({ ...prev, swiftCode: value }));
      },
    },
    {
      type: "text",
      label: "Bank Address",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.bankAddress ?? "",
      placeholder: "Bank Address",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, bankAddress: stringValue }));
      },
    },
    {
      type: "text",
      label: "Bank Phone Number",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.bankPhoneNumber ?? "",
      placeholder: "Bank Phone Number",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, bankPhoneNumber: stringValue }));
      },
    },
    {
      type: "text",
      label: "Account Number",
      bgColor: COLORS.WhiteBackground,
      value: dialogData.accountNumber ?? "",
      placeholder: "Account Number",
      onChange: (value: string | number) => {
        const stringValue =
          typeof value === "string" ? value : value.toString();
        setDialogData((prev) => ({ ...prev, accountNumber: stringValue }));
      },
    },
    {
      type: "text" as const,
      hidden: true,
      value: dialogData.website ?? "",
      onChange: (value: any) => {},
    },
  ],
];
